import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDING_SPOTS = 500;

const ROLES = [
  { id: "buyer", label: "I've been burned as a buyer" },
  { id: "builder", label: "I buy and want to help build this" },
  { id: "seller", label: "I sell honestly and want fair reviews" },
];

export const Signup = () => {
  const [role, setRole] = useState("buyer");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/early-access/count`)
      .then(({ data }) => setCount(data.count))
      .catch(() => setCount(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus({ state: "error", msg: "That email doesn't look right — check it and try again." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/early-access`, { email, role });
      setStatus({ state: "success", msg: data.message });
      if (data.position) setCount(data.position);
      setEmail("");
    } catch (err) {
      const d = err.response?.data?.detail;
      setStatus({ state: "error", msg: typeof d === "string" ? d : "Something went wrong on our side. Try once more?" });
    }
  };

  return (
    <section id="join" data-testid="signup-section" className="py-24 sm:py-32 bg-white border-t border-black/5">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Stop paying to find out.{" "}
            <em className="not-italic text-trust">Start knowing before you buy.</em>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">
            Leave your email to get in first when it launches — and be one of the founding reviewers whose receipts
            protect the next buyer.
          </p>

          <div data-testid="role-picker" className="mt-9 flex flex-col sm:flex-row gap-3">
            {ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`role-picker-${r.id}`}
                onClick={() => setRole(r.id)}
                aria-pressed={role === r.id}
                className={`flex-1 rounded-full border px-5 py-3 text-sm font-semibold transition-colors duration-200 ${
                  role === r.id
                    ? "border-trust bg-trust text-white"
                    : "border-gray-200 bg-paper text-gray-600 hover:border-trust/50 hover:text-trust"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5">
            <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="signup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              aria-label="Email address"
              className="flex-1 rounded-full border border-gray-200 bg-paper px-6 py-4 text-base outline-none transition-colors duration-200 focus:border-trust focus:bg-white placeholder:text-gray-400"
            />
            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={status.state === "loading"}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold px-8 py-4 transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60"
            >
              {status.state === "loading" ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  Get early access
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
            </div>
            <label className="mt-4 flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer">
              <input data-testid="signup-consent-checkbox" type="checkbox" required className="mt-0.5 accent-trust w-4 h-4" />
              <span>
                I agree to the{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-trust font-semibold underline underline-offset-2">Privacy Policy</a>{" "}
                and{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-trust font-semibold underline underline-offset-2">Terms of Use</a>
              </span>
            </label>
          </form>

          {status.msg && (
            <p
              data-testid="signup-message"
              role="status"
              className={`mt-4 flex items-center gap-2 text-sm font-semibold ${
                status.state === "success" ? "text-verified" : "text-warn"
              }`}
            >
              {status.state === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              {status.msg}
            </p>
          )}

          {count !== null && (
            <div data-testid="signup-counter" className="mt-8">
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-sm font-semibold">
                  <span className="font-display text-2xl font-bold text-trust">{count}</span>{" "}
                  <span className="text-gray-500">of {FOUNDING_SPOTS} founding spots claimed</span>
                </p>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold">
                  {Math.max(FOUNDING_SPOTS - count, 0)} left
                </p>
              </div>
              <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-trust"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((count / FOUNDING_SPOTS) * 100, 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-gray-500">
            Free forever for reviewers. <b className="text-ink">First 500 signups</b> help decide which sellers get
            checked first.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
